import React, { useState } from "react";
import ChecklistCard from "../../ChecklistCard.js";
import "../../../styles/ResidentialListing.css";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import importantNotes from "../../../data/BritishColumbia/importantNotes.js";
import ImportantNotes from "../../ImportantNotes.js";
import BackToTop from "../../BackToTop.js";
import ClauseCard from "../../ClauseCard.js";
import clauseCardDesc from "../../../data/BritishColumbia/clauseCardDescription.js";
import clauseData from "../../../data/BritishColumbia/clauseData.js";

export default function AssignmentListing() {
  const [ListingData, setListingData] = useState([
    "Disclosure of Representation in Trading Services",
    "Multiple Listing Contract",
    "Original Purchase Contract",
    "Title Search (upload to MLS System)",
    "Privacy Notice & Consent Form",
    "Property Disclosure Statement or PNDS",
    "Related FINTRAC Documents",
    "Disclosure to Seller of Expected Remuneration (before Offer Acceptance)",
    "Full REALTOR® MLS Printout",
  ]);

  const [ListingType, setListingType] = useState("MLS");
  const [IsDisclosure, setIsDisclosure] = useState("No");
  const [Is244, setIs244] = useState("No");

  // State for conditional documents that can be added/removed
  const [conditionalDocuments, setConditionalDocuments] = useState({
    "Are you placing a lockbox on the property?": false,
    "Do you or immediate family members own the Contract?": false,
    "Do you have a delayed offer presentation date?": false,
    "Co-Listing with Joint Representation": false,
    "Co-Listing with Separate Representation": false,
  });

  // Mapping of checkbox labels to their corresponding listing data strings
  const documentMapping = {
    "Are you placing a lockbox on the property?":
      "Lockbox Acknowledgement, Consent, Release, and Indemnity",
    "Do you or immediate family members own the Contract?":
      "Disclosure of Interest in Trade (BCFSA Form)",
    "Do you have a delayed offer presentation date?":
      "Disclosure Regarding Presentations of Offers",
    "Co-Listing with Joint Representation":
      "Co-Listing Form - Joint Representation (only add co-listing agents)",
    "Co-Listing with Separate Representation":
      "Co-Listing Form - Separate Representation (only add co-listing agents)",
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: "#506CAA",
        dark: "#506CAA",
      },
      text: {
        primary: "#31303F",
        secondary: "#506CAA",
      },
    },
  });

  // Function to handle conditional document checkboxes
  const toggleConditionalDocument = (documentName) => {
    setConditionalDocuments((prev) => {
      const newState = {
        ...prev,
        [documentName]: !prev[documentName],
      };

      // Handle mutual exclusivity for co-listing options
      if (
        documentName === "Co-Listing with Joint Representation" &&
        newState[documentName]
      ) {
        newState["Co-Listing with Separate Representation"] = false;
      } else if (
        documentName === "Co-Listing with Separate Representation" &&
        newState[documentName]
      ) {
        newState["Co-Listing with Joint Representation"] = false;
      }

      // Get the corresponding string for the listing data
      const listingDataString = documentMapping[documentName];

      // Update Is244 state if this is the delayed offer presentation checkbox
      if (documentName === "Do you have a delayed offer presentation date?") {
        setIs244(newState[documentName] ? "Yes" : "No");
      }
      if (
        documentName === "Do you or immediate family members own the property?"
      ) {
        setIsDisclosure(newState[documentName] ? "Yes" : "No");
      }

      // Update the listing data based on checkbox state
      setListingData((prevListingData) => {
        let newListingData = [...prevListingData];

        // Remove both co-listing forms first
        newListingData = newListingData.filter(
          (item) =>
            item !==
              "Co-Listing Form - Joint Representation (only add co-listing agents)" &&
            item !==
              "Co-Listing Form - Separate Representation (only add co-listing agents)"
        );

        // Add the appropriate co-listing form if checked
        if (newState["Co-Listing with Joint Representation"]) {
          newListingData.push(
            "Co-Listing Form - Joint Representation (only add co-listing agents)"
          );
        } else if (newState["Co-Listing with Separate Representation"]) {
          newListingData.push(
            "Co-Listing Form - Separate Representation (only add co-listing agents)"
          );
        }

        // Handle other documents
        if (newState[documentName] && !documentName.includes("Co-Listing")) {
          if (!newListingData.includes(listingDataString)) {
            newListingData.push(listingDataString);
          }
        } else if (!documentName.includes("Co-Listing")) {
          newListingData = newListingData.filter(
            (item) => item !== listingDataString
          );
        }

        return newListingData;
      });

      return newState;
    });
  };

  const handleListingTypeChange = (type) => {
    setListingType(type);
    let newListingData = [...ListingData];

    if (type === ListingType) {
      return;
    } else if (type === "Exclusive") {
      newListingData = newListingData.filter(
        (item) => item !== "Full REALTOR® MLS Printout"
      );
      newListingData[1] = "Exclusive Listing Contract";
      newListingData[3] = "Title Search";
    } else if (type === "MLS") {
      if (!newListingData.includes("Full REALTOR® MLS Printout")) {
        newListingData.push("Full REALTOR® MLS Printout");
      }
      newListingData[1] = "Multiple Listing Contract";
      newListingData[3] = "Title Search (upload to MLS System)";
    }
    setListingData(newListingData);
  };

  return (
    <div className="checklists w-full flex flex-col items-center justify-center">
      <div className="flex flex-col w-full pl-4 ">
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 ">MLS or Exclusive Listing?</div>
          <ThemeProvider theme={theme}>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                defaultValue="MLS"
              >
                <FormControlLabel
                  value="MLS"
                  control={
                    <Radio
                      size="small"
                      onClick={() => handleListingTypeChange("MLS")}
                    />
                  }
                  label="MLS"
                />
                <FormControlLabel
                  value="Exclusive"
                  control={
                    <Radio
                      size="small"
                      onClick={() => handleListingTypeChange("Exclusive")}
                    />
                  }
                  label="Exclusive"
                />
              </RadioGroup>
            </FormControl>
          </ThemeProvider>
        </div>

        {/* Check all that apply section */}
        <div className="flex flex-col items-left pb-4">
          <div className="pr-5 pb-2 ">
            For each "Yes" response, check the box that applies:
          </div>
          <ThemeProvider theme={theme}>
            <div className="flex flex-col ">
              {Object.keys(conditionalDocuments).map((documentName) => (
                <FormControlLabel
                  key={documentName}
                  control={
                    <Checkbox
                      size="small"
                      checked={conditionalDocuments[documentName]}
                      onChange={() => toggleConditionalDocument(documentName)}
                    />
                  }
                  label={documentName}
                />
              ))}
            </div>
          </ThemeProvider>
        </div>
      </div>

      {ListingType === "Exclusive" ? (
        <>
          <br />
          <div className="w-full pl-1 flex justify-left">
            <p>
              <strong>Important Note:</strong> The CREA Co-operation Policy does
              not apply to assignments where the transfer of title, as in the
              case of pre-construction assignments, has not taken place.
              However, many Builder's Contracts restrict the advertisement of
              pre-construction assignments. Check the Builder Purchase Contract
              and connect with the Builder to ensure you are not breaching the
              original Purchase Conract.
            </p>
          </div>
        </>
      ) : (
        ""
      )}
      <ChecklistCard
        province={"ON"}
        Title1={""}
        CheckListData={ListingData}
        Header={ListingType + " Listing:"}
        Header2={"Assignor Client (Original Purchaser)"}
      />
      <ClauseCard
        ClauseCardDescription={clauseCardDesc.filter((item) =>
          item.category.includes("resListing")
        )}
        ClauseDataCommon={clauseData.filter(
          (item) => item.category.includes("forsale") && item.isCommon === "yes"
        )}
        ClauseDataSituational={clauseData.filter(
          (item) => item.category.includes("forsale") && item.isCommon === "no"
        )}
      />
      <div className="important-notes flex flex-col w-full justify-left pt-6">
        <strong>Important Notes:</strong>
        <div className="pb-3"></div>
        {Is244 === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("directions") === true
            )}
          />
        ) : (
          ""
        )}
        {ListingType === "Exclusive" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("exclusiveListing") === true
            )}
          />
        ) : (
          ""
        )}
        {IsDisclosure === "Yes" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("disclosure") === true
            )}
          />
        ) : (
          ""
        )}
        <ImportantNotes
          Notes={importantNotes.filter(
            (notes) => notes.category.includes("assignListing") === true
          )}
        />
        <ImportantNotes
          Notes={importantNotes.filter(
            (notes) => notes.category.includes("listing") === true
          )}
        />
        {ListingType === "MLS" ? (
          <ImportantNotes
            Notes={importantNotes.filter(
              (notes) => notes.category.includes("mls") === true
            )}
          />
        ) : (
          ""
        )}
        <BackToTop />
      </div>
    </div>
  );
}
